import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../../../utilities/ServiceFetch.js';
/**
 * This class handles adding items to the One Web cart.
 *
 * GraphQL Playground: https://dev.favs.oneweb.ingkadt.com/playground.
 */
export default class IngkaShoppingListService {
    /**
     * Constructor.
     *
     * @param options Options.
     * @param options.endpoint Endpoint.
     * @param options.clientId Client ID.
     * @param options.clientSecret Client secret.
     * @param options.itemGroupingInBagEnabled Setting if grouping in bag is enabled on the market.
     */
    constructor(options) {
        this.endpoint = options.endpoint;
        this.clientId = options.clientId;
        this.clientSecret = options.clientSecret;
        this.itemGroupingInBagEnabled = options.itemGroupingInBagEnabled;
    }
    /**
     * Adds items to shopping list.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.token One web token.
     * @param options.shoppingListName List name.
     * @param options.designCode Design code (VPC-code) to group articles in checkout and assembly services.
     * @param options.singleItems Single items to be added individually outside the group.
     * @returns Promise object.
     */
    async addToList(options) {
        let listId = await this.getListId({
            token: options.token,
            shoppingListName: options.shoppingListName
        });
        if (!listId) {
            listId = await this.createList({
                token: options.token,
                shoppingListName: options.shoppingListName
            });
        }
        await this.setSelectedList({ token: options.token, listId });
        return await this.addItemsToList({
            items: options.items,
            token: options.token,
            listId,
            shoppingListName: options.shoppingListName,
            designCode: options.designCode,
            singleItems: options.singleItems
        });
    }
    /**
     * Returns list count.
     *
     * @param options Options..
     * @param options.token One web token.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async getListCount(options) {
        const response = await ServiceFetch.json(this.endpoint, {
            credentials: 'same-origin',
            method: 'POST',
            headers: this.getHeaders(options.token),
            body: this.getListQuantityBody()
        });
        const data = response.data;
        if (!data || !Array.isArray(data.lists)) {
            throw new ServiceException('Failed to get list count. No lists in response.');
        }
        const list = data.lists.find((list) => list.name === options.shoppingListName);
        return list ? list.quantity : 0;
    }
    /**
     * Sets the shopping list which the latest item was added to as selected.
     *
     * @param options ...
     * @param options.token One web token.
     * @param options.listId List id.
     * @returns Promise object.
     */
    async setSelectedList(options) {
        await ServiceFetch.json(this.endpoint, {
            credentials: 'same-origin',
            method: 'POST',
            headers: this.getHeaders(options.token),
            body: this.getSelectedListBody(options.listId)
        });
    }
    /**
     * Returns list id.
     *
     * @param options Options.
     * @param options.token One web token.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async getListId(options) {
        const response = await ServiceFetch.json(this.endpoint, {
            credentials: 'same-origin',
            method: 'POST',
            headers: this.getHeaders(options.token),
            body: this.getListIdBody()
        });
        const data = response.data;
        if (!data || !Array.isArray(data.lists)) {
            throw new ServiceException('Failed to get list id. No lists in response.');
        }
        const list = data.lists.find((list) => list.name === options.shoppingListName);
        return list ? list.listId : null;
    }
    /**
     * Creates a list and returns the ID.
     *
     * @param options Options.
     * @param options.token One web token.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async createList(options) {
        const response = await ServiceFetch.json(this.endpoint, {
            credentials: 'same-origin',
            method: 'POST',
            headers: this.getHeaders(options.token),
            body: this.getCreateListBody(options.shoppingListName)
        });
        const data = response.data;
        if (!data || !data.createList || !data.createList.listId) {
            throw new ServiceException('Failed to create list. Missing "listId" in response.');
        }
        return data.createList.listId;
    }
    /**
     * Adds item to a shopping list.
     *
     * @param options Options.
     * @param options.items Array of shopping items.
     * @param options.token One web token.
     * @param options.listId List name.
     * @param options.designCode Design Code.
     * @param options.singleItems Single items to be added.
     * @param options.shoppingListName Name to fetch list count.
     * @returns Promise object.
     */
    async addItemsToList(options) {
        const response = await ServiceFetch.json(this.endpoint, {
            credentials: 'same-origin',
            method: 'POST',
            headers: this.getHeaders(options.token),
            body: this.getAddItemsBody(options.listId, options.items, options.designCode, options.singleItems)
        });
        const listCount = (await this.getListCount({
            token: options.token,
            shoppingListName: options.shoppingListName
        })) ?? null;
        return {
            listCount,
            errorList: this.getErrorList(response)
        };
    }
    getListIdBody() {
        return JSON.stringify({
            operationName: null,
            variables: {},
            query: '{ lists {name, listId} }'
        });
    }
    getListQuantityBody() {
        return JSON.stringify({
            operationName: null,
            variables: {},
            query: '{ lists {name, quantity} }'
        });
    }
    getAddItemsBody(listId, items, designCode, singleItems) {
        let mutation = '';
        const addItemsAsGroup = !!designCode && this.itemGroupingInBagEnabled;
        if (!addItemsAsGroup) {
            mutation = this.getAddItemsMutation(items, listId);
        }
        else {
            if (singleItems) {
                mutation = this.getAddItemsMutation(singleItems, listId);
            }
            const addDesignsMutation = `addDesigns(designs: [{configurationId: "${designCode}", quantity: 1}], listId: "${listId}") { listId, name }`;
            mutation = mutation
                ? `${mutation}
				${addDesignsMutation}`
                : addDesignsMutation;
        }
        return JSON.stringify({
            operationName: null,
            variables: {},
            query: `mutation {${mutation}}`
        });
    }
    getAddItemsMutation(items, listId) {
        const itemsString = `[${items.map((item) => {
            return `{ itemNo: "${item.id}", quantity: ${item.quantity} }`;
        })}]`;
        return `addItems(items: ${itemsString}, listId: "${listId}" ) { quantity }`;
    }
    getSelectedListBody(id) {
        return JSON.stringify({
            operationName: null,
            variables: {},
            query: `mutation { setSelected(listId: "${id}" ) { context { userId } } }`
        });
    }
    getCreateListBody(name) {
        return JSON.stringify({
            operationName: null,
            variables: {},
            query: `mutation { createList(name: "${name}") { listId } }`
        });
    }
    /**
     * Returns an error list.
     *
     * @param response Response.
     * @returns Error list.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getErrorList(response) {
        const errors = response.errors;
        if (errors) {
            return errors.map((error) => {
                return {
                    code: error.extensions ? error.extensions.code : null,
                    message: error.message,
                    items: error.extensions && error.extensions.data && error.extensions.data.itemNos
                        ? error.extensions.data.itemNos
                        : null
                };
            });
        }
        return [];
    }
    getHeaders(token) {
        const headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token.value,
            Accept: 'application/json',
            'x-client-id': this.clientId,
            'x-client-secret': this.clientSecret
            // The below can be used for accessing GraphQL Playground:
            // 'x-user-id': '2008c852-8b33-48f6-b5ef-45add754cdf5',
            // 'x-is-anonymous': true,
        };
        return headers;
    }
}
//# sourceMappingURL=IngkaShoppingListService.js.map